import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TopBar from './layout/TopBar';
import BottomNav from './BottomNav';
import Phone from './Phone';
import { C } from './theme';
import { fmtCurrency, pick, useDrillAuth } from './drillHelpers';
import HourlyBarChart from './reports/components/charts/reusable/HourlyBarChart';
import { NetSalesShimmer, Bone } from './Shimmer';
import { useDesktopTopBar } from '../context/DesktopLayoutContext';
import { useIsDesktop } from './reports/reportTokens';
import { getSalesSummary, todayStr, yesterdayStr, getStartEndFromFilter } from '../services/reportsApi';
import { useAppSelector, useAppDispatch } from '../redux/hooks';
import { fetchHourlySalesRequest } from '../redux/reports/clickHouse/clickHouseActions';
import type { HourlySalesResponse } from '../interface/clickHouseInterface';

const DrillNetSales = ({ onBack, initialDateFilter, initialDate, snapshotValues: snapshotValuesProp, snapshotKpisLoading }: { onBack?: () => void; initialDateFilter?: string; initialDate?: string; snapshotValues?: any; snapshotKpisLoading?: boolean } = {} as any) => {
    const isEmbedded = !!onBack;
    const navigate = useNavigate();
    const isDesktop = useIsDesktop();

    useEffect(() => {
        if (!isEmbedded && isDesktop) navigate('/reports/snapshot', { replace: true });
    }, [isDesktop, navigate, isEmbedded]);
    const setTopBarProps = useDesktopTopBar();
    const dispatch = useAppDispatch();
    const { locationId, token, snapshotValues: snapshotValuesFromAuth } = useDrillAuth();
    const snapshotValues = snapshotValuesProp ?? snapshotValuesFromAuth;
    const today = useMemo(() => todayStr(), []);
    const yesterday = useMemo(() => yesterdayStr(), []);
    const [selectedDate, setSelectedDate] = useState(initialDate ?? today);
    const [activeDateFilter, setActiveDateFilter] = useState(initialDateFilter ?? 'Today');
    const isToday = activeDateFilter === 'Today';
    const dateRange = useMemo(() => {
        const range = getStartEndFromFilter(activeDateFilter, today, yesterday);
        return range || { startDate: selectedDate, endDate: selectedDate };
    }, [activeDateFilter, selectedDate, today, yesterday]);

    // Unified hourly source — same Redux state + endpoint as the desktop Net Sales drill
    // (fetchHourlySalesRequest handles Today → live, historical → date-range, both via /sales/live/v2/hourly-sales).
    const hourlySalesData: HourlySalesResponse | null = useAppSelector((s) => s.clickHouse.hourlySalesData);
    const hourlySalesLoading: boolean = useAppSelector((s) => s.clickHouse.hourlySalesLoading);
    const hourlySalesFailure: boolean = useAppSelector((s) => s.clickHouse.hourlySalesFailure);
    const snapshotDetailsLoading: boolean = useAppSelector((s) => s.clickHouse.snapshotDetailsLoading);
    // While snapshot-details refetches (date/channel change), snapshotValues.netSales holds the
    // previous range's value — shimmer the KPI instead of flashing stale data (SSE-18).
    // Embedded gets the parent's flag (also covers its legacy-summary fallback window);
    // standalone keys off the redux flag directly.
    const kpisLoading = snapshotKpisLoading ?? snapshotDetailsLoading;

    // Standalone (non-embedded) view fetches its own hourly data. When embedded, the parent
    // SnapshotScreen already dispatches the same request — avoid a duplicate call.
    useEffect(() => {
        if (!isEmbedded && locationId) {
            dispatch(fetchHourlySalesRequest({ locationId, startDate: dateRange.startDate, endDate: dateRange.endDate }));
        }
    }, [isEmbedded, locationId, dateRange.startDate, dateRange.endDate]); // eslint-disable-line react-hooks/exhaustive-deps

    const [loading, setLoading] = useState(true);

    const handleDateFilterChange = useCallback((opt: string) => {
        setActiveDateFilter(opt);
        if (opt === 'Today') setSelectedDate(today);
        else if (opt === 'Yesterday') setSelectedDate(yesterday);
    }, [today, yesterday]);

    useEffect(() => {
        if (isEmbedded) return;
        setTopBarProps({
            title: 'Net Sales',
            onBack: () => navigate('/reports/snapshot'),
            dateFilters: { value: activeDateFilter, onChange: handleDateFilterChange },
        });
    }, [setTopBarProps, activeDateFilter, handleDateFilterChange, navigate, isEmbedded]);

    const [summary, setSummary] = useState<any>(null);

    useEffect(() => {
        if (!locationId) {
            setLoading(false);
            return;
        }
        // Embedded view: snapshotValues.netSales already comes from clickhouse — skip non-clickhouse fetch
        if (isEmbedded) {
            setLoading(false);
            return;
        }
        const opts = { locationId, startDate: dateRange.startDate, endDate: dateRange.endDate, token };
        setLoading(true);
        getSalesSummary({ ...opts })
            .then((r) => setSummary(r ?? null))
            .catch(() => setSummary(null))
            .finally(() => setLoading(false));
    }, [locationId, token, dateRange.startDate, dateRange.endDate, isToday, isEmbedded]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Hourly Revenue (itemTotal) — unified for Today and historical ──
    const hourlyHours = hourlySalesData?.hours ?? [];
    const operatingStartHour = hourlySalesData?.operatingStartHour ?? 6;
    const operatingEndHour = hourlySalesData?.operatingEndHour ?? 23;
    const operatingHourCount = hourlySalesData?.operatingHourCount ?? 0;
    const hourlyRevenueChartData: { label: string; value: number }[] = hourlyHours.map(h => {
        const suffix = h.hourValue >= 12 ? ' PM' : ' AM';
        const disp = h.hourValue % 12 || 12;
        return { label: `${disp}${suffix}`, value: h.itemTotal };
    });
    const revenuePeakValue = hourlyRevenueChartData.reduce((m, d) => Math.max(m, d.value), 0);
    const revenuePeakIndex = revenuePeakValue > 0 ? hourlyRevenueChartData.findIndex(d => d.value === revenuePeakValue) : -1;
    const peakHour = revenuePeakIndex >= 0 ? (hourlyRevenueChartData[revenuePeakIndex]?.label ?? '--') : '--';
    const windowHours = hourlyHours.filter(h => h.hourValue >= operatingStartHour && h.hourValue <= operatingEndHour);
    const avgPerHour = operatingHourCount > 0
        ? windowHours.reduce((s, h) => s + h.itemTotal, 0) / operatingHourCount
        : 0;
    const hourlyTotal = hourlyRevenueChartData.reduce((s, d) => s + (Number(d.value) || 0), 0);

    const apiNetSales = pick(summary, 'totalNetSales', 'netSales', 'totalSales', 'net_sales', 'total_sales', 'revenue');
    const apiNetSalesNum = apiNetSales != null && apiNetSales !== '' ? Number(apiNetSales) : null;
    const snapshotNetSales = snapshotValues?.netSales != null ? Number(snapshotValues.netSales) : null;
    // hourlyTotal is a last-resort fallback: when it would be the display source and the
    // hourly fetch is still in flight, keep shimmering instead of flashing $0.00 → total (SSE-18).
    const resolvedNetSales = (snapshotNetSales != null && snapshotNetSales > 0) ? snapshotNetSales
        : (apiNetSalesNum != null && apiNetSalesNum > 0) ? apiNetSalesNum
        : null;
    const netSales = resolvedNetSales ?? (hourlyTotal > 0 ? hourlyTotal : null);
    const netSalesLoading = kpisLoading || (resolvedNetSales == null && hourlySalesLoading);
    const totalStr = fmtCurrency(netSales ?? 0);

    const displayPeakHour = hourlySalesLoading ? <Bone w={60} h={16} r={4} style={{ margin: '0 auto' }} /> : hourlySalesFailure ? '--' : peakHour;

    const subTitle = `${totalStr} total`;

    const inner = loading ? <NetSalesShimmer /> : (<>
        <div style={{ fontSize: 10, color: C.t3, padding: '0 16px 4px' }}>{netSalesLoading ? <Bone w={80} h={12} r={4} /> : subTitle}</div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 16px' }}>
                <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                    {[
                        { l: 'Net Sales', v: netSalesLoading ? <Bone w={60} h={16} r={4} style={{ margin: '0 auto' }} /> : totalStr },
                        { l: 'Peak Hour', v: displayPeakHour },
                        { l: 'Avg/Hour', v: hourlySalesLoading ? <Bone w={60} h={16} r={4} style={{ margin: '0 auto' }} /> : fmtCurrency(avgPerHour) },
                    ].map((k, i) => (
                        <div key={i} style={{ flex: 1, background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 10, padding: '8px 6px', textAlign: 'center', boxShadow: C.shadow }}>
                            <div style={{ fontSize: 7, color: C.t3, textTransform: 'uppercase' }}>{k.l}</div>
                            <div style={{ fontSize: 16, fontWeight: 700, color: i === 1 ? C.accent : C.t1, marginTop: 2 }}>{k.v}</div>
                        </div>
                    ))}
                </div>
                <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 14, padding: '14px 12px', marginBottom: 12, boxShadow: C.shadow }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: C.t1, marginBottom: 8 }}>Hourly Revenue</div>
                    <HourlyBarChart
                        data={hourlyRevenueChartData}
                        loading={hourlySalesLoading}
                        error={hourlySalesFailure}
                        onRetry={() => dispatch(fetchHourlySalesRequest({ locationId, startDate: dateRange.startDate, endDate: dateRange.endDate }))}
                        kpiName="hourly revenue"
                        barColor={C.accent}
                        chartHeight={80}
                        showCurrency={true}
                        minHeight={120}
                        avgOverride={avgPerHour}
                    />
                </div>
        </div>
    </>);

    if (isEmbedded) return <>{inner}</>;
    return (
        <Phone>
            <TopBar variant="mobile" />
            {inner}
            <BottomNav />
        </Phone>
    );
};

export default DrillNetSales;
